/* ==========================================================================
   nma-diagnostico.js — motor e interfaz del diagnóstico
   Ref visual: mockup.html §#diag (fuente de verdad de layout y comportamiento)
   Ref motor:  bactoboost-spec-etapa2-v1.md §1, §4

   QUÉ VIENE DE DÓNDE
   - Del mockup: las 6 preguntas, sus pesos, los cortes de banda, el medidor de
     arco que se mueve con cada respuesta, el progreso por puntos, un clic por
     opción y el índice de salud (alto = sano).
   - De la spec: el motor de dos ejes. El mockup produce un solo número y §1.1
     lo descarta explícitamente: un puntaje dice cuán grave está el sistema, no
     qué le pasa.

   DOS ESCALAS, UNA SOLA CUENTA
   El motor calcula SEVERIDAD (alto = grave) porque es lo que consume el
   catálogo por metafield. La interfaz muestra SALUD = 100 − severidad, que es
   lo que hace el mockup. Los cortes coinciden: salud 75/50/28 es severidad
   25/50/72.

   FUNCIÓN PURA
   `calcular(respuestas, config)` no toca el DOM ni guarda nada. Eso permite
   correr la verificación anti-sesgo de §4.4 sin navegador.

   Sin librerías, sin backend, sin localStorage (§4.6).

   ⚠️ EL CALENDARIO Y EL ENVÍO POR CORREO SALIERON DE ALCANCE (ítem 2.6).
   Decisión del cliente: implicaban T&C y papeleo fuera del proyecto. Se
   retiraron el bloque de fechas, el formulario de captura de correo y el punto
   de integración del proveedor.

   Lo que NO se fue es el eje de duración: el resultado sigue diciendo por
   cuánto tiempo se sostiene el tratamiento —"unos 12 meses"— porque eso sale
   de la aritmética de la ficha y del catálogo §3.3, no de agendar fechas.
   Ver CUSTOM.md §33.

   🔴 LOS PESOS Y CORTES NO ESTÁN VALIDADOS TÉCNICAMENTE (§4.1). Ver CUSTOM.md §9.
   ========================================================================== */

(() => {
  if (window.NmaDiagnostico) return;

  /* Vocabulario cerrado (§2.2). Si el motor deriva algo que no está acá, cae a
     mantencion-preventiva y lo avisa. */
  const DOLENCIAS_VALIDAS = [
    'dren-saturado',
    'olor',
    /* Renombrada en la v1.4: el producto PREVIENE obstrucciones, no las
       destapa (§1.1 de Etapa 3). El nombre viejo era `obstruccion` y prometía
       justo lo que el apartado "Qué no hace" descarta. */
    'prevencion-obstruccion',
    /* `infraestructura` —edificio o condominio con estanque de acopio— se retiró
       el 02-09-2026 (CUSTOM.md §44). Ningún producto del catálogo la declaraba,
       así que era el 20 % de los recorridos cayendo a una grilla de productos
       domésticos. Un edificio en zona urbana está conectado a la red igual que
       una casa: se rige por alcantarillado, y va en `ciudad`. */
    'trampa-grasera',
    'mantencion-preventiva',
    'planta-tratamiento',
  ];
  const DOLENCIA_POR_DEFECTO = 'mantencion-preventiva';

  /* ────────────────────────────────────────────────────────────────────
     §5.8.1 · TECHO DURO de la dosis de intervención, en gramos.

     Vive acá y no en la config a propósito: la config es justo lo que se
     puede editar, así que un techo configurable no es un techo. La ficha
     dice "máximo 3 kilos" en la alternativa 4, y ese número no lo mueve
     nadie desde el personalizador ni desde un metaobjeto.

     Y ojo con la distinción que corrige la v1.5: esto es un LÍMITE, no una
     dosis. La dosis de intervención de cada dolencia se edita en su bloque
     (`dosis_intervencion_gramos`); si alguien pone un valor mayor que este,
     el motor lo rechaza y aplica el techo.
     ──────────────────────────────────────────────────────────────────── */
  const TECHO_INTERVENCION_G = 3000;

  const PREGUNTAS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'];

  const OPCIONES = {
    p1: ['fosa', 'ptas', 'ciudad', 'comercio'],
    p2: ['menos-6m', '6m-2a', 'mas-2a', 'nunca'],
    p3: ['no', 'a-veces', 'permanente'],
    p4: ['normal', 'lento', 'estanca'],
    p5: ['1-3', '4-6', '7-mas'],
    p6: ['nunca', 'alguna-vez', 'frecuente'],
  };

  /* ────────────────────────────────────────────────────────────────────
     CUSTOM.md §38 · VARIANTES DE PREGUNTA POR SISTEMA.

     La p5 le preguntaba a todo el mundo cuántas personas usan el sistema a
     diario. Para una fosa es la pregunta correcta —la ficha dosifica por
     usuarios— pero para una casa conectada al alcantarillado no significa
     nada: ahí no hay un sistema que reciba carga de personas, hay desagües
     que se tratan de a uno. La pregunta que corresponde es cuántos.

     Acá viven solo los VALORES, para que `recorridos()` enumere el universo
     real y no combinaciones que nadie puede contestar. El texto visible y
     cuál variante se muestra viven en los bloques de la sección: cada bloque
     `pregunta` declara para qué sistemas es.
     ──────────────────────────────────────────────────────────────────── */
  const OPCIONES_POR_SISTEMA = {
    ciudad: {
      p5: ['1-4', '5-9', '10-mas'],
    },
  };

  function opcionesDe(clave, sistema) {
    const porSistema = OPCIONES_POR_SISTEMA[sistema];
    return (porSistema && porSistema[clave]) || OPCIONES[clave];
  }

  /* ────────────────────────────────────────────────────────────────────
     CUSTOM.md §38 · VOCABULARIO DE LAS BANDAS POR SISTEMA.

     "Saturación" describe un dren que dejó de infiltrar en la tierra: es lo
     que le pasa a una fosa y a un pozo absorbente. En una casa conectada al
     alcantarillado no hay dren que saturar —el agua se va a la red— hay
     cañerías que acumulan, y la palabra correcta es "acumulación".

     Esto es SOLO TEXTO. El id de la banda, sus cortes de severidad y todo lo
     que decide producto y dosis siguen siendo los mismos: acá no se ramifica
     el motor, se ramifica cómo se le nombra al visitante lo que le pasa.

     Es el nivel 3 del repliegue: los bloques `banda_sistema` de la sección
     pisan estos valores campo por campo.
     ──────────────────────────────────────────────────────────────────── */
  const BANDAS_POR_SISTEMA = {
    ciudad: {
      sano: {
        nombre: 'Sin señales de acumulación',
        nota: 'Por lo que nos cuentas, no aparecen señales de acumulación en tus desagües. Con mantención regular las redes domiciliarias suelen mantenerse así.',
      },
      atencion: {
        nombre: 'Requiere atención',
        nota: 'Por lo que nos cuentas, aparecen señales tempranas de acumulación en tus desagües. En estos casos se suele recomendar actuar antes de que avance.',
      },
      /* Ojo con el texto de estas dos: en alcantarillado domiciliario la
         dolencia es `prevencion-obstruccion`, que declara 0 g de
         intervención. La banda es alta pero NO hay tratamiento de choque que
         ofrecer, así que la nota no puede prometer uno. */
      avanzada: {
        nombre: 'Acumulación avanzada',
        nota: 'Por lo que nos cuentas, la acumulación en tus desagües está avanzada. Conviene tratarla ahora y no dejar que siga creciendo.',
      },
      colapso: {
        nombre: 'Acumulación crítica',
        nota: 'Por lo que nos cuentas, las respuestas apuntan a una acumulación importante. Si el agua ya se devuelve, además de tratarlo conviene que lo revisemos contigo: escríbenos.',
      },
    },
  };

  const CONFIG_POR_DEFECTO = {
    /* Pesos del mockup. La pregunta 1 no aporta severidad: define el sistema. */
    pesos: {
      p2: { 'menos-6m': 0, '6m-2a': 12, 'mas-2a': 24, nunca: 30 },
      p3: { no: 0, 'a-veces': 10, permanente: 22 },
      p4: { normal: 0, lento: 14, estanca: 26 },
      /* Dos escalas en la misma pregunta: usuarios para las ramas con carga
         de personas, puntos de desagüe para alcantarillado domiciliario
         (CUSTOM.md §38). Los valores no se pisan entre sí porque son
         distintos, y el motor suma por valor, no por posición.

         LOS TRAMOS DE DESAGÜES VALEN CERO, y es a propósito. Cuántos puntos
         querés tratar es ALCANCE, no gravedad: alguien con diez desagües
         sanos no tiene un sistema más enfermo que alguien con dos. La primera
         versión les copió el perfil de la carga por usuarios (0 · 5 · 10) y
         eso movía la banda en 45 de los 108 casos de esa rama y daba vuelta
         el Ultra en 22 — la pregunta de contexto decidiendo el diagnóstico.

         La p5 de ciudad queda entonces como la p1: se pregunta, se guarda, no
         suma. Sirve para dimensionar la conversación y para el día que la
         ficha defina dosis por punto de aplicación, que es el hueco de §5.7.2.
         Decisión de Nico, 01-09-2026. */
      p5: { '1-3': 0, '4-6': 5, '7-mas': 10, '1-4': 0, '5-9': 0, '10-mas': 0 },
      /* Los químicos suman porque la soda cáustica y los destapadores matan la
         fauna bacteriana: el sistema necesita más producto para repoblarse.
         Es lo primero que hay que validar técnicamente. */
      p6: { nunca: 0, 'alguna-vez': 6, frecuente: 12 },
    },

    techo: 100,
    /* El arco nunca llega a cero: el mockup deja un piso de 5 de salud para que
       el medidor no quede vacío del todo. Es cosmético, no cambia la banda. */
    salud_minima: 5,

    /* Bandas en severidad. Equivalen a los cortes de salud del mockup:
       salud ≥75 → sev ≤25 · ≥50 → ≤50 · ≥28 → ≤72 · resto. */
    bandas: [
      /* Los nombres describen lo observado, no certifican ni pronostican
         (§4.3). "Sistema sano" y "Riesgo de colapso" están prohibidos.
         `ultra` es el eje A decidiendo concentración; `intervencion` es el eje
         A decidiendo si además de mantención corresponde una intervención
         (§5.8). Solo las bandas 3 y 4 la reciben: un sistema sin señales de
         saturación NO recibe una intervención de 3 kg. */
      { id: 'sano', nombre: 'Sin señales de saturación', min: 0, max: 25, ultra: false, intervencion: false },
      { id: 'atencion', nombre: 'Requiere atención', min: 26, max: 50, ultra: false, intervencion: false },
      { id: 'avanzada', nombre: 'Saturación avanzada', min: 51, max: 72, ultra: true, intervencion: true },
      { id: 'colapso', nombre: 'Saturación crítica', min: 73, max: 100, ultra: true, intervencion: true },
    ],

    /* Síntoma dominante: el escurrimiento manda sobre el olor. Un dren que no
       traga es más urgente que un olor, y además suele explicarlo. */
    sintomas: [
      { si: { pregunta: 'p4', distinto_de: 'normal' }, sintoma: 'escurrimiento' },
      { si: { pregunta: 'p3', distinto_de: 'no' }, sintoma: 'olor' },
    ],
    sintoma_por_defecto: 'ninguno',

    /* Tabla de decisión: sistema + síntoma dominante → dolencia.
       Reemplaza al `tipo` del mockup, que solo servía para pisar el nombre del
       producto. Se evalúa en orden; `'*'` es comodín. */
    dolencias: [
      { sistema: 'fosa', sintoma: 'escurrimiento', dolencia: 'dren-saturado' },
      { sistema: 'fosa', sintoma: 'olor', dolencia: 'olor' },
      { sistema: 'fosa', sintoma: 'ninguno', dolencia: 'mantencion-preventiva' },
      { sistema: 'ptas', sintoma: '*', dolencia: 'planta-tratamiento' },
      { sistema: 'ciudad', sintoma: '*', dolencia: 'prevencion-obstruccion' },
      { sistema: 'comercio', sintoma: '*', dolencia: 'trampa-grasera' },
    ],

    /* ────────────────────────────────────────────────────────────────────
       EJE C · de la carga declarada al envase (§5.7).
       Fuente: FICHA_TECNICA_BACTOBOOST_2025. No es estimación.

       El envase NO sale de la severidad (§1.2.1): la ficha dosifica por
       cantidad de usuarios. Una fosa de dos personas en estado crítico
       necesita 500 g más seguido y en Ultra, no un balde.

         · Fosa séptica ........ 500 g por cada 2–6 usuarios, mensual
         · Planta de tratam. ... 1 kg por cada 10 usuarios, cada 15 días
         · Estanque de ciudad .. 1 kg por m³, cada 7 días
         · Alcantarillado dom. .. la ficha no le asigna dosis por usuario
         · Comercio ............ SIN DATO en la ficha (§5.7.2)

       `techo` es el máximo que la ficha admite para esa carga. La
       verificación de sobredosificación compara el envase resuelto contra
       este número, no contra el envase propuesto: si alguien edita la
       matriz desde el personalizador y se pasa, el motor lo recorta.
       ──────────────────────────────────────────────────────────────────── */
    dosificacion: [
      { sistema: 'fosa', carga: '1-3', envase: 500, techo: 500 },
      { sistema: 'fosa', carga: '4-6', envase: 500, techo: 500 },
      /* 500 g cubren hasta 6 usuarios, así que 7 o más pide 1 kg. Por encima
         de 12 usuarios 1 kg subdosifica, que es el lado seguro del error. */
      { sistema: 'fosa', carga: '7-mas', envase: 1000, techo: 1000 },
      /* §10.2 del catálogo (confirmado, sept 2026): una planta domiciliaria
         lleva "la misma dosis que le corresponda POR TRAMO, repartida en 2
         aplicaciones al mes". O sea el mismo tramo por personas que la fosa,
         no un kilo fijo.

         Antes era `carga: '*'` con 1 kg para todos, que venía de la regla vieja
         de "1 kg por cada 10 usuarios" del §4.3. Con 4-6 personas eso
         dosificaba el DOBLE de lo que corresponde. */
      { sistema: 'ptas', carga: '1-3', envase: 500, techo: 500 },
      { sistema: 'ptas', carga: '4-6', envase: 500, techo: 500 },
      { sistema: 'ptas', carga: '7-mas', envase: 1000, techo: 1000 },
      /* Alcantarillado doméstico · POR PUNTOS DE APLICACIÓN, no por usuarios.

         Estaba en `carga: '*'` con `afirma_dosis: false`, o sea 500 g fijos y
         sin decir la cantidad, contestara lo que contestara la persona. Con eso
         la p5 de ciudad —"¿cuántos puntos o desagües querés tratar?"— se
         preguntaba y no movía nada: ni la banda (§38.3, a propósito) ni el
         envase (esto, que sí era un hueco).

         La ficha lo tiene resuelto y `landings.js` ya lo verifica (catálogo
         §4.2): **500 g diluidos cubren 4 puntos** —WC, lavamanos, lavaplatos,
         ducha— y **más de 4 puntos pide 1 kg**. Son las dos únicas filas que
         la ficha declara, así que son las dos únicas que se afirman.

         `afirma_dosis: true` en esos dos tramos porque ahora SÍ hay una
         cantidad respaldada que decir. Antes no la había. */
      { sistema: 'ciudad', carga: '1-4', envase: 500, techo: 500 },
      { sistema: 'ciudad', carga: '5-9', envase: 1000, techo: 1000 },
      /* Este tramo YA TIENE RESPALDO. El comentario anterior decía que la
         ficha no llegaba hasta acá y por eso no se afirmaba la dosis. §10.1 del
         catálogo (confirmado, sept 2026) trae la fila que faltaba:

           3 baños + cocina · 10 puntos · 1,25 kg → se lleva 1,5 kg
                                                   (3 frascos de 500 g)

         Así que ahora sí se afirma, y con el envase correcto. Antes mostraba
         1 kg, que para 10 puntos queda corto por un cuarto de kilo. */
      { sistema: 'ciudad', carga: '10-mas', envase: 1500, techo: 1500 },
      /* Repliegue de la rama: mientras las variantes por sistema no estén en la
         plantilla, la p5 que llega es la de usuarios (1-3 · 4-6 · 7-mas). Sin
         esta fila esos recorridos se quedaban sin envase. Conserva el
         comportamiento viejo —500 g sin afirmar— para no afirmar una dosis por
         puntos a partir de una respuesta que habla de personas. */
      { sistema: 'ciudad', carga: '*', envase: 500, techo: 500, afirma_dosis: false },
      /* §5.7.2: la ficha no da dosis periódica para trampas graseras. Se
         muestra el balde por volumen y se deriva a asesoría, pero el motor
         NO afirma una dosis. `sin_respaldo` lo marca para la verificación. */
      { sistema: 'comercio', carga: '*', envase: 3000, techo: 3000, afirma_dosis: false, sin_respaldo: true },
    ],

    /* ────────────────────────────────────────────────────────────────────
       §5.8 · DOS REGÍMENES, DOS TECHOS.

       La ficha dosifica de dos maneras distintas y la v1.3 las trataba como
       una sola, con el resultado de que el diagnóstico dejó de recomendar los
       productos de intervención. No estaban sobredimensionados: se estaban
       midiendo con la regla de mantención.

         · Mantención  — por usuarios, periódica. Techo: el de `dosificacion`,
                         máximo 1 kg fuera de comercios.
         · Intervención — por método, evento sobre un sistema saturado.
                         Techo: 3 kg, de la alternativa 4 de la ficha.

       La intervención es la puerta por la que podría volver el sesgo
       comercial, así que tiene dos llaves: la banda la habilita (solo 3 y 4)
       y la dolencia declara su dosis (0 = sin intervención respaldada). Un
       sistema sin señales de saturación NO recibe intervención.
       ──────────────────────────────────────────────────────────────────── */
    intervencion: {
      /* §10.3 del catálogo, versión confirmada de sept 2026:
           Semana 1: 1 kg de Ultra, de una vez
           Semana 2: 1 kg de Ultra, de una vez
           La recuperación queda lista en 2 semanas.

         Antes eran 4 aplicaciones semanales ("todos los viernes del primer
         mes"), que venía de la guía digital y del §4.5 viejo. §10 lo reemplaza
         y manda sobre §4 en dosis, cadencia y modo de aplicación. */
      dias: 7,
      aplicaciones: 2,
    },

    /* ────────────────────────────────────────────────────────────────────
       CUÁNTOS MESES DE TRATAMIENTO · catálogo §3.3

       El motor resolvía cuánto y cada cuánto, pero nunca por cuánto tiempo. Y
       el catálogo §2 es explícito: los multipacks **se venden por duración, no
       por gramos** — un pack de 4 frascos no son "2 kg", son "4 meses de
       tratamiento". Sin este eje el resultado no habla el idioma en el que
       está armado el catálogo.

       ⚠️ Esto NO es el calendario del ítem 2.6, que salió de alcance. Es una
       frase, no fechas: "el tratamiento se sostiene unos 12 meses". No agenda
       nada, no pide correo y no promete recordatorios.

       El número no se inventa: sale de los dos planes por perfil de §3.3.

         · hasta 6 usuarios .... Plan anual · 500 g al mes × 12 = 6 kg
         · más de 6 usuarios ... Plan semestral · 1 kg al mes × 6 = 6 kg

       Los dos son EL MISMO pack de 12 frascos presentado distinto. Eso da una
       invariante verificable: con la dosis mensual que resuelve el eje C, la
       aritmética tiene que devolver 6 kg en los dos casos.
       ──────────────────────────────────────────────────────────────────── */
    meses_por_carga: {
      '1-3': 12,
      '4-6': 12,
      '7-mas': 6,
    },

    /* ⚠️ §3.3 SOLO DEFINE PLANES PARA FOSA SÉPTICA.

       Los dos planes por perfil —anual y semestral— están escritos para fosas.
       No hay plan documentado para plantas de tratamiento, y la aritmética sin
       esta guarda da números que nadie firmó: PTAS se dosifica cada 15 días,
       así que 12 meses × 1 kg × 2 aplicaciones = 24 kg. Es correcto como
       cuenta y es inventado como recomendación.

       Las demás ramas ya no afirman duración porque no afirman dosis
       (`afirmaDosis: false`), pero PTAS sí la afirma — de ahí que haga falta
       nombrar explícitamente dónde hay plan. */
    sistemas_con_plan: ['fosa'],

    /* ────────────────────────────────────────────────────────────────────
       §5.8.1 · Dosis de intervención por dolencia, en gramos.

       Antes esto era el techo de 3 kg aplicado como dosis: el motor prescribía
       el máximo permitido en vez de una cantidad decidida. Para una fosa de
       1–3 personas, cuya mantención es 500 g, eran seis veces la dosis
       mensual — y el matiz de "hasta 3 kg" no sobrevive al carro, porque el
       visitante agrega un envase y se lleva los 3 kg.

       Ahora es un valor por dolencia, editable desde su bloque, para que el
       validador técnico pueda bajarlo sin tocar código. Mismo patrón de §4.1.

       0 = esta dolencia no tiene intervención respaldada. Es el caso de las
       dos ramas sin dosis en la ficha: alcantarillado doméstico no tiene fosa
       ni dren que recuperar, y comercios no tiene dato. Antes era una lista blanca de sistemas en el código; como
       valor por dolencia se puede revisar sin pasar por un desarrollador.

       La ficha NO da fórmula para escalar la intervención por carga, así que
       acá no se inventa una: son valores fijos y auditables. Cualquiera de
       ellos por encima de TECHO_INTERVENCION_G se rechaza.
       ──────────────────────────────────────────────────────────────────── */
    dosis_intervencion: {
      /* 2 kg de Ultra, que es lo que §10.3 define como la recuperación. Antes
         eran 3000 g, del protocolo de tres semanas que §10 dejó atrás.

         El número decide QUÉ PACK se recomienda: con 3000 el motor elegía un
         pack de 3 kg; con 2000 elige el Pack Intervención Inicial, que es
         exactamente el que la versión confirmada nombra para recuperar.

         El techo de TECHO_INTERVENCION_G sigue en 3 kg: es el máximo de la
         ficha, no la dosis. Son cosas distintas. */
      'dren-saturado': 2000,
      olor: 2000,
      'mantencion-preventiva': 2000,
      'planta-tratamiento': 2000,
      'prevencion-obstruccion': 0,
      'trampa-grasera': 0,
    },
  };

  function pesosDesdePreguntas(preguntas) {
    if (!preguntas || !preguntas.length) return null;
    const pesos = {};
    for (const p of preguntas) {
      if (!p.clave || !p.opciones) continue;
      const tabla = {};
      let hay = false;
      for (const o of p.opciones) {
        if (typeof o.puntos === 'number') {
          tabla[o.valor] = o.puntos;
          hay = true;
        }
      }
      /* Se fusiona en vez de reemplazar: desde el 01-09-2026 una clave puede tener
         varias variantes (CUSTOM.md §38) y cada bloque trae solo las opciones de la
         suya. Reemplazar dejaba a la primera variante sin puntaje. */
      if (hay) pesos[p.clave] = Object.assign({}, pesos[p.clave], tabla);
    }
    return Object.keys(pesos).length ? pesos : null;
  }

  /* La tabla de dolencias se evalúa en orden: la primera regla que calza gana,
     así que los comodines tienen que quedar después de las específicas.
     El orden de `metaobjects.<tipo>.values` no está documentado, por eso cada
     regla trae su `orden` y se ordena acá. Estable: ante empate se respeta la
     posición original. Ver CUSTOM.md §12. */
  function ordenarReglas(reglas) {
    if (!reglas || !reglas.length) return reglas;
    return reglas
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const oa = typeof a.r.orden === 'number' ? a.r.orden : a.i;
        const ob = typeof b.r.orden === 'number' ? b.r.orden : b.i;
        return oa === ob ? a.i - b.i : oa - ob;
      })
      .map((x) => x.r);
  }

  /* Nivel 1 del repliegue para las bandas: si el metaobjeto trae entradas,
     pisan a las de los settings de la sección. Se fusiona por `id`, así que
     una banda cargada a medias no borra a las demás. */
  function fusionarBandas(desdeSettings, desdeMetaobjeto) {
    if (!desdeMetaobjeto || !desdeMetaobjeto.length) return desdeSettings;
    const base = (desdeSettings || []).map((b) => Object.assign({}, b));
    for (const mo of desdeMetaobjeto) {
      if (!mo || !mo.id) continue;
      const i = base.findIndex((b) => b.id === mo.id);
      const limpia = {};
      ['nombre', 'min', 'max'].forEach((k) => {
        if (mo[k] !== null && mo[k] !== undefined && mo[k] !== '') limpia[k] = mo[k];
      });
      if (i === -1) base.push(Object.assign({ id: mo.id }, limpia));
      else base[i] = Object.assign(base[i], limpia);
    }
    return base.sort((a, b) => a.min - b.min);
  }

  /* CUSTOM.md §38 · Nivel 3 → 2 del repliegue para el vocabulario por sistema.
     `base` son los valores de este archivo; `encima`, lo que emitieron los
     bloques `banda_sistema` de la sección. Se fusiona campo por campo para
     que una entrada cargada a medias —solo el nombre, sin la nota— no borre
     la nota que ya había. */
  function fusionarVocabulario(base, encima) {
    const salida = {};
    for (const sistema of Object.keys(base || {})) {
      salida[sistema] = {};
      for (const id of Object.keys(base[sistema])) {
        salida[sistema][id] = Object.assign({}, base[sistema][id]);
      }
    }
    for (const sistema of Object.keys(encima || {})) {
      salida[sistema] = salida[sistema] || {};
      for (const id of Object.keys(encima[sistema] || {})) {
        const v = encima[sistema][id] || {};
        const actual = Object.assign({}, salida[sistema][id]);
        if (v.nombre) actual.nombre = v.nombre;
        if (v.nota) actual.nota = v.nota;
        salida[sistema][id] = actual;
      }
    }
    return salida;
  }

  function fusionarConfig(config) {
    if (!config) return CONFIG_POR_DEFECTO;
    const D = CONFIG_POR_DEFECTO;
    const derivados = pesosDesdePreguntas(config.preguntas);
    const bandas = fusionarBandas(
      config.bandas && config.bandas.length ? config.bandas : D.bandas,
      config.bandas_mo
    );
    return {
      pesos: Object.assign({}, D.pesos, derivados || config.pesos || {}),
      techo: typeof config.techo === 'number' ? config.techo : D.techo,
      salud_minima:
        typeof config.salud_minima === 'number' ? config.salud_minima : D.salud_minima,
      bandas: reponerConcentracion(bandas),
      sintomas: config.sintomas && config.sintomas.length ? config.sintomas : D.sintomas,
      sintoma_por_defecto: config.sintoma_por_defecto || D.sintoma_por_defecto,
      // Nivel 3 del repliegue: si no vino nada del metaobjeto ni de los
      // bloques, se usan las 6 reglas por defecto de este archivo.
      dolencias: ordenarReglas(
        config.dolencias && config.dolencias.length ? config.dolencias : D.dolencias
      ),
      dosificacion:
        config.dosificacion && config.dosificacion.length ? config.dosificacion : D.dosificacion,
      intervencion: Object.assign({}, D.intervencion, config.intervencion || {}),
      /* Se fusiona por dolencia en vez de reemplazar el objeto entero: si el
         schema emite una sola dolencia a medias, las otras no se pierden. */
      dosis_intervencion: Object.assign(
        {},
        D.dosis_intervencion,
        config.dosis_intervencion || {}
      ),
      meses_por_carga: Object.assign({}, D.meses_por_carga, config.meses_por_carga || {}),
      sistemas_con_plan:
        config.sistemas_con_plan && config.sistemas_con_plan.length
          ? config.sistemas_con_plan
          : D.sistemas_con_plan,
    };
  }

  /* Las bandas que emite la sección traen id/nombre/min/max, no la
     concentración. Sin esto, una config desde el schema borraría `ultra` e
     `intervencion`: todo el mundo terminaría en Bactoboost-120 y nadie
     recibiría intervención. */
  function reponerConcentracion(bandas) {
    return bandas.map((b) => {
      if (typeof b.ultra === 'boolean' && typeof b.intervencion === 'boolean') return b;
      const base = CONFIG_POR_DEFECTO.bandas.find((d) => d.id === b.id);
      return Object.assign({}, b, {
        ultra: typeof b.ultra === 'boolean' ? b.ultra : !!(base && base.ultra),
        intervencion:
          typeof b.intervencion === 'boolean' ? b.intervencion : !!(base && base.intervencion),
      });
    });
  }

  /* ────────────────────────────────────────────────────────────────────
     EJE C · resuelve el envase para el sistema y la carga declarados.
     Devuelve también el techo, para que quien consuma esto pueda verificar
     que no se pasó, y `afirma_dosis` para las ramas sin dato en la ficha.
     ──────────────────────────────────────────────────────────────────── */
  function resolverEnvase(respuestas, cfg) {
    const sistema = respuestas.p1;
    const carga = respuestas.p5;
    const tabla = cfg.dosificacion || [];

    // Primero la fila exacta; después el comodín. Nunca al revés, o el
    // comodín de un sistema taparía sus propias filas por carga.
    const fila =
      tabla.find((f) => f.sistema === sistema && f.carga === carga) ||
      tabla.find((f) => f.sistema === sistema && f.carga === '*') ||
      null;

    if (!fila) {
      return { gramos: 0, techo: 0, afirmaDosis: false, sinRespaldo: true, sinRegla: true };
    }

    const techo = typeof fila.techo === 'number' ? fila.techo : fila.envase;
    // El recorte es la última defensa: si la matriz se edita desde el
    // personalizador y queda por encima de la ficha, gana la ficha.
    const gramos = Math.min(fila.envase, techo);

    return {
      gramos: gramos,
      techo: techo,
      recortado: gramos !== fila.envase,
      afirmaDosis: fila.afirma_dosis !== false,
      sinRespaldo: fila.sin_respaldo === true,
      sinRegla: false,
    };
  }

  function calcularSeveridad(respuestas, cfg) {
    let total = 0;
    for (const p of PREGUNTAS) {
      const tabla = cfg.pesos[p];
      if (!tabla) continue;
      const puntos = tabla[respuestas[p]];
      if (typeof puntos === 'number') total += puntos;
    }
    return Math.max(0, Math.min(cfg.techo, total));
  }

  function bandaDe(severidad, cfg) {
    return (
      cfg.bandas.find((b) => severidad >= b.min && severidad <= b.max) ||
      cfg.bandas[cfg.bandas.length - 1]
    );
  }

  function sintomaDominante(respuestas, cfg) {
    for (const regla of cfg.sintomas) {
      const valor = respuestas[regla.si.pregunta];
      if (valor === undefined) continue;
      if (regla.si.distinto_de !== undefined && valor !== regla.si.distinto_de) return regla.sintoma;
      if (regla.si.valor !== undefined && valor === regla.si.valor) return regla.sintoma;
    }
    return cfg.sintoma_por_defecto;
  }

  function derivarDolencia(respuestas, cfg) {
    const sistema = respuestas.p1;
    const sintoma = sintomaDominante(respuestas, cfg);

    const regla = cfg.dolencias.find(
      (r) => r.sistema === sistema && (r.sintoma === sintoma || r.sintoma === '*')
    );

    if (!regla) {
      return { dolencia: DOLENCIA_POR_DEFECTO, sintoma, fueraDeVocabulario: true, motivo: 'sin regla' };
    }
    if (DOLENCIAS_VALIDAS.indexOf(regla.dolencia) === -1) {
      return {
        dolencia: DOLENCIA_POR_DEFECTO,
        sintoma,
        fueraDeVocabulario: true,
        motivo: 'valor "' + regla.dolencia + '" fuera del vocabulario de §2.2',
      };
    }
    return { dolencia: regla.dolencia, sintoma, fueraDeVocabulario: false, motivo: null };
  }

  /* ────────────────────────────────────────────────────────────────────
     §5.8, §5.8.1 y §5.8.2 · Resuelve si corresponde una intervención, y de
     cuánto.

     TRES llaves, las tres necesarias:
       1 · la banda la habilita (solo 3 y 4)
       2 · la dolencia declara una dosis mayor que 0
       3 · el visitante declaró al menos un síntoma  ← nueva en la v1.6

     La tercera cierra el peor hueco que quedaba: 2 de las 1.620 combinaciones
     llegaban a banda 3 sin ningún síntoma —sin olor y con escurrimiento
     normal— por acumulación de mantención vencida, carga alta y químicos. Son
     0,1 % del volumen y son el peor 0,1 % posible: alguien que reporta que su
     sistema anda bien recibiendo una propuesta de intervención de 3 kg. En
     `mantencion-preventiva` era además una contradicción con el propio nombre
     de la dolencia.

     La severidad de esas combinaciones NO se toca: el sistema puede estar
     degradado y el resultado sigue diciéndolo. Lo que cambia es que se le
     ofrece mantención en vez de intervención.

     El síntoma se lee de `sintomaDominante`, la misma función que decide la
     dolencia, en vez de volver a mirar p3 y p4 acá. Con la tabla por defecto
     las dos formas son equivalentes —`'ninguno'` ⟺ p3 = "no" ∧ p4 = "normal",
     que es literal lo que pide §5.8.2— pero leerlo de la tabla evita una
     segunda definición de "síntoma" que pueda derivar de la primera. Y si la
     tabla quedara vacía, todo cae en `'ninguno'` y nadie recibe intervención:
     el error va hacia el lado seguro.

     Devolver `gramos: 0` significa "no corresponde intervención".

     El techo de TECHO_INTERVENCION_G es duro. Un valor mayor —del schema, de
     un metaobjeto o de una plantilla editada a mano— se rechaza y queda
     marcado en `recortada`, para que la verificación pueda verlo en vez de
     que pase inadvertido.
     ──────────────────────────────────────────────────────────────────── */
  function resolverIntervencion(cfg, banda, dolencia, sintoma) {
    const tabla = cfg.dosis_intervencion || {};
    const declarada = parseInt(tabla[dolencia], 10);
    const bruta = isNaN(declarada) || declarada < 0 ? 0 : declarada;

    // El rechazo se evalúa siempre, incluso si la banda no habilita: así un
    // valor fuera de rango se reporta aunque hoy no llegue a usarse.
    const recortada = bruta > TECHO_INTERVENCION_G;
    const admisible = recortada ? TECHO_INTERVENCION_G : bruta;

    const habilitadaPorBanda = !!(banda && banda.intervencion);
    const respaldadaPorDolencia = admisible > 0;
    // Tercera llave (§5.8.2): sin síntoma declarado no hay intervención, por
    // alta que sea la severidad.
    const haySintoma = !!sintoma && sintoma !== cfg.sintoma_por_defecto;

    const corresponde = habilitadaPorBanda && respaldadaPorDolencia && haySintoma;

    return {
      gramos: corresponde ? admisible : 0,
      declarada: bruta,
      techo: TECHO_INTERVENCION_G,
      recortada: recortada,
      corresponde: corresponde,
      /* Por qué no corresponde, para que la verificación distinga los tres
         motivos en vez de ver un 0 sin explicación. */
      motivo: corresponde
        ? null
        : !habilitadaPorBanda
          ? 'banda'
          : !respaldadaPorDolencia
            ? 'sin dosis declarada'
            : 'sin sintoma declarado',
    };
  }

  /* ────────────────────────────────────────────────────────────────────
     Cuánto producto consume el tratamiento completo, en gramos.

     Es aritmética, no prescripción: dosis por aplicación × aplicaciones al
     mes × meses. Sirve para decir "este pack alcanza para N meses" en vez de
     "trae 6 kg", que es lo que pide el catálogo §2.

     `frecuenciaDias` viene de la dolencia y es la única fuente de cadencia del
     proyecto (§6.1). 0 = sin cadencia documentada → no se afirma nada.
     ──────────────────────────────────────────────────────────────────── */
  function gramosDelTratamiento(res, frecuenciaDias) {
    const dias = parseInt(frecuenciaDias, 10) || 0;
    if (!dias || !res.mesesTratamiento || !res.envaseGramos || !res.afirmaDosis) return 0;
    // Un mes son 30 días en toda la aritmética del proyecto: mezclar 30 y 30,4
    // daría números que no cierran con los planes de la ficha.
    const porMes = 30 / dias;
    return Math.round(res.envaseGramos * porMes * res.mesesTratamiento);
  }

  /**
   * Motor. Función pura. Tres ejes (§1.2):
   *   A · severidad → concentración (Ultra o no) y si el primer mes es intensivo
   *   B · dolencia  → producto y método de aplicación
   *   C · carga     → tamaño del envase
   * @returns {{severidad, salud, banda, dolencia, sintoma, envaseGramos,
   *            esUltra, afirmaDosis, fueraDeVocabulario, completo}}
   */
  function calcular(respuestas, config) {
    const cfg = fusionarConfig(config);
    const r = respuestas || {};

    const severidad = calcularSeveridad(r, cfg);
    const d = derivarDolencia(r, cfg);
    const banda = bandaDe(severidad, cfg);
    const envase = resolverEnvase(r, cfg);
    /* La intervención necesita la dolencia (para su dosis declarada) y el
       síntoma dominante (tercera llave de §5.8.2). Los dos salen de `d`. */
    const intervencion = resolverIntervencion(cfg, banda, d.dolencia, d.sintoma);

    return {
      severidad,
      // Lo que ve el visitante: alto = sano, como en el mockup.
      salud: Math.max(cfg.salud_minima, 100 - severidad),
      banda: banda,
      dolencia: d.dolencia,
      sintoma: d.sintoma,

      // Eje C. `envaseGramos` es 0 cuando no hay regla para el sistema.
      envaseGramos: envase.gramos,
      envaseTecho: envase.techo,
      envaseRecortado: !!envase.recortado,
      // Las ramas sin dato en la ficha muestran producto pero no afirman
      // cuánto ni cada cuánto (§5.7.2 y el caso doméstico de §5.7.1).
      afirmaDosis: envase.afirmaDosis,
      sinRespaldoFicha: envase.sinRespaldo,

      /* Cuánto tiempo dura el tratamiento (§3.3). Sale de la carga, no de la
         gravedad: la duración la fija cuánto consume el sistema por mes, y la
         gravedad ya decide concentración y si además hay intervención. */
      mesesTratamiento:
        (cfg.sistemas_con_plan || []).indexOf(r.p1) === -1
          ? 0
          : parseInt((cfg.meses_por_carga || {})[r.p5], 10) || 0,

      // Eje A aplicado a concentración y a si corresponde intervenir.
      esUltra: !!banda.ultra,

      /* Régimen de intervención (§5.8). `intervencionGramos` es 0 cuando no
         corresponde — siempre en bandas 1 y 2, y en las dolencias que declaran
         0. El techo de mantención de arriba NO lo limita: son dos regímenes
         con dos techos.

         `intervencionDeclarada` es lo que pidió la config antes del recorte, y
         `intervencionRecortada` avisa que se rechazó por pasarse del techo
         duro. Los dos existen para que la verificación distinga "está bien"
         de "estaba mal y lo tapamos". */
      hayIntervencion: intervencion.corresponde,
      intervencionGramos: intervencion.gramos,
      intervencionDeclarada: intervencion.declarada,
      intervencionTecho: intervencion.techo,
      intervencionRecortada: intervencion.recortada,
      // 'banda' · 'sin dosis declarada' · 'sin sintoma declarado' · null
      motivoSinIntervencion: intervencion.motivo,
      // Tercera llave de §5.8.2, expuesta para la verificación.
      sintomaDeclarado: !!d.sintoma && d.sintoma !== cfg.sintoma_por_defecto,

      fueraDeVocabulario: d.fueraDeVocabulario,
      motivoFallback: d.motivo,
      completo: PREGUNTAS.every((p) => typeof r[p] === 'string' && r[p].length > 0),
    };
  }

  /** Todas las combinaciones posibles. Verificación anti-sesgo de §4.4.

      El universo se enumera POR RAMA: desde el 01-09-2026 la p5 tiene una variante
      por sistema (CUSTOM.md §38), así que un producto cartesiano plano inventaría
      combinaciones que nadie puede contestar —un alcantarillado con "7 o más
      personas"— y dejaría fuera las que sí existen. */
  function recorridos() {
    const salida = [];
    for (const sistema of OPCIONES.p1) {
      (function avanzar(i, parcial) {
        if (i === PREGUNTAS.length) return salida.push(parcial);
        const clave = PREGUNTAS[i];
        for (const valor of opcionesDe(clave, sistema)) {
          avanzar(i + 1, Object.assign({}, parcial, { [clave]: valor }));
        }
      })(1, { p1: sistema });
    }
    return salida;
  }

  function distribucion(config, filtro) {
    const cfg = fusionarConfig(config);
    const conteo = {};
    cfg.bandas.forEach((b) => (conteo[b.id] = 0));
    const sev = [];
    let total = 0;

    for (const r of recorridos()) {
      if (filtro && !filtro(r)) continue;
      const res = calcular(r, cfg);
      conteo[res.banda.id]++;
      sev.push(res.severidad);
      total++;
    }

    sev.sort((a, b) => a - b);
    const pct = (n) => Math.round((n / total) * 1000) / 10;
    const ultima = cfg.bandas[cfg.bandas.length - 1];

    return {
      total,
      conteo,
      porcentajes: cfg.bandas.reduce((a, b) => ((a[b.id] = pct(conteo[b.id])), a), {}),
      minimo: sev[0],
      maximo: sev[sev.length - 1],
      mediana: sev[Math.floor(sev.length / 2)],
      promedio: Math.round((sev.reduce((a, b) => a + b, 0) / total) * 10) / 10,
      // §4.4: más de 40% en la banda más grave significa que los pesos están mal
      superaLimiteCritico: pct(conteo[ultima.id]) > 40,
    };
  }

  /* ======================================================================
     Interfaz. Reproduce el comportamiento del mockup: puntos de progreso,
     un clic por opción, "← Volver", y el arco que se mueve con cada
     respuesta en vez de esperar al final.
     ====================================================================== */

  const LARGO_ARCO = 267; // stroke-dasharray del path del mockup

  class NmaDiagnosticoElemento extends HTMLElement {
    connectedCallback() {
      // El orden importa: leerTextos() consulta this.config para fusionar las
      // bandas que vengan del metaobjeto.
      this.config = this.leerConfig();
      this.textos = this.leerTextos();
      this.paso = 0;
      this.respuestas = {};
      this.historial = [];
      this.reaccion = '';
      this.delta = 0;
      this.saludPintada = null;
      this.saltoComercio = false;

      this.puntos = this.querySelector('[data-progreso]');
      this.caja = this.querySelector('[data-caja]');
      this.btnVolver = this.querySelector('[data-volver]');
      this.arco = this.querySelector('[data-arco]');
      this.numero = this.querySelector('[data-numero]');
      this.marcaDelta = this.querySelector('[data-delta]');
      this.estado = this.querySelector('[data-estado]');
      this.nota = this.querySelector('[data-nota]');
      this.rx = this.querySelector('[data-rx]');
      this.candidatos = Array.from(this.querySelectorAll('[data-candidato]'));
      this.avisarCatalogoMudo();

      // Todas las variantes que emitió Liquid; `preguntas` es la selección
      // que corresponde al sistema declarado en la p1 (CUSTOM.md §38).
      this.todasLasPreguntas = this.leerPreguntas();
      this.preguntas = this.resolverPreguntas();
      if (!this.preguntas.length) return;

      this.btnVolver?.addEventListener('click', () => this.volver());
      this.caja?.addEventListener('click', (e) => {
        const b = e.target.closest('[data-opcion]');
        if (b) this.elegir(b.dataset.opcion);
        if (e.target.closest('[data-reiniciar]')) this.reiniciar();
      });

      this.querySelector('[data-rx-agregar]')?.addEventListener('click', () => this.agregarAlCarro());

      this.pintar();
    }

    leerConfig() {
      const t = this.querySelector('script[data-config]');
      if (!t) return null;
      try {
        return JSON.parse(t.textContent);
      } catch (e) {
        console.warn('[nma-diagnostico] config inválida, se usan los valores por defecto', e);
        return null;
      }
    }

    leerTextos() {
      const t = this.querySelector('script[data-textos]');
      let txt;
      try {
        txt = t ? JSON.parse(t.textContent) : {};
      } catch (e) {
        return {};
      }

      /* Repliegue de tres niveles para el copy que sí se migró.
         Metaobjeto → bloques/settings de la sección → constantes del JS.
         Se fusiona campo por campo: una entrada cargada a medias no borra lo
         que ya había. Ver CUSTOM.md §12. */
      const bandasMo = (this.config && this.config.bandas_mo) || [];
      if (bandasMo.length) {
        txt.bandas = Object.assign({}, txt.bandas);
        txt.dosis_por_banda = Object.assign({}, txt.dosis_por_banda);
        for (const b of bandasMo) {
          if (!b || !b.id) continue;
          const actual = Object.assign({}, txt.bandas[b.id]);
          if (b.nombre) actual.nombre = b.nombre;
          if (b.nota) actual.nota = b.nota;
          txt.bandas[b.id] = actual;
          if (b.dosis) txt.dosis_por_banda[b.id] = b.dosis;
        }
      }

      const dolMo = txt.dolencias_mo || [];
      if (dolMo.length) {
        txt.dolencias = Object.assign({}, txt.dolencias);
        for (const d of dolMo) {
          if (!d || !d.valor) continue;
          const actual = Object.assign({}, txt.dolencias[d.valor]);
          if (d.coleccion_url) actual.coleccion_url = d.coleccion_url;
          if (d.coleccion_titulo) actual.coleccion_titulo = d.coleccion_titulo;
          if (typeof d.frecuencia_dias === 'number') actual.frecuencia_dias = d.frecuencia_dias;
          if (d.nota) actual.nota = d.nota;
          txt.dolencias[d.valor] = actual;
        }
      }

      /* CUSTOM.md §38 · vocabulario por sistema, con el mismo repliegue que el resto.
         La sección lo emite como lista plana —sistema, banda, nombre, nota—
         porque agrupar por dos claves en Liquid es incómodo; acá se agrupa. */
      const vocab = {};
      for (const v of txt.bandas_por_sistema_lista || []) {
        if (!v || !v.sistema || !v.banda) continue;
        vocab[v.sistema] = vocab[v.sistema] || {};
        vocab[v.sistema][v.banda] = { nombre: v.nombre, nota: v.nota };
      }
      txt.bandas_por_sistema = fusionarVocabulario(BANDAS_POR_SISTEMA, vocab);

      return txt;
    }

    /* Las preguntas se leen de plantillas del DOM: el texto lo escribe la
       clienta desde el personalizador, no está en el JS. */
    leerPreguntas() {
      return Array.from(this.querySelectorAll('template[data-pregunta]')).map((t) => ({
        clave: t.dataset.pregunta,
        categoria: t.dataset.categoria || '',
        texto: t.dataset.texto || '',
        /* CUSTOM.md §38 · para qué sistemas sirve esta variante. Vacío = para todos,
           que es como quedan las preguntas que no se ramifican. */
        sistemas: (t.dataset.sistemas || '')
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
        opciones: Array.from(t.content.querySelectorAll('[data-valor]')).map((o) => ({
          valor: o.dataset.valor,
          texto: o.textContent.trim(),
          reaccion: o.dataset.reaccion || '',
        })),
      }));
    }

    /* CUSTOM.md §38 · Una sola variante por clave: la del sistema declarado.

       Se resuelve POR CLAVE y no filtrando la lista a propósito. Filtrando,
       el total de pasos cambiaría a mitad del recorrido según lo que el
       visitante conteste en la p1 —los puntos de progreso saltarían y el
       "Paso 5 de 6" mentiría—. Resolviendo por clave, seis preguntas son
       seis, conteste lo que conteste; lo único que cambia es cuál se muestra.

       Antes de responder la p1 no hay sistema, así que gana la variante sin
       restricción, y si no hay ninguna, la primera declarada. */
    resolverPreguntas() {
      const sistema = this.respuestas.p1;
      const claves = [];
      const variantes = {};
      for (const q of this.todasLasPreguntas || []) {
        if (!variantes[q.clave]) {
          variantes[q.clave] = [];
          claves.push(q.clave);
        }
        variantes[q.clave].push(q);
      }
      return claves.map((c) => {
        const v = variantes[c];
        const propia = sistema && v.find((q) => q.sistemas.indexOf(sistema) !== -1);
        return propia || v.find((q) => !q.sistemas.length) || v[0];
      });
    }

    /* Rehace la selección de variantes y descarta las respuestas que dejaron
       de ser contestables.

       El descarte es la parte que importa: sin él, cambiar la p1 con
       "← Volver" dejaría en `respuestas` un valor de la variante vieja —p5 =
       "4-6" en una rama que ahora pregunta por desagües— y el motor lo
       sumaría igual, porque suma por valor. Hoy `volver()` ya va borrando lo
       que deshace, así que en la práctica no queda nada que limpiar; esto
       existe para que siga siendo cierto si mañana se agrega otra forma de
       volver atrás. */
    reencuadrar() {
      this.preguntas = this.resolverPreguntas();
      const validos = {};
      for (const q of this.preguntas) validos[q.clave] = q.opciones.map((o) => o.valor);
      for (const clave of Object.keys(this.respuestas)) {
        const lista = validos[clave];
        if (!lista || lista.indexOf(this.respuestas[clave]) !== -1) continue;
        delete this.respuestas[clave];
        const i = this.historial.indexOf(clave);
        if (i !== -1) this.historial.splice(i, 1);
      }
    }

    get terminado() {
      return this.paso >= this.preguntas.length;
    }

    elegir(valor) {
      const q = this.preguntas[this.paso];
      if (!q) return;

      const antes = calcular(this.respuestas, this.config);
      this.respuestas[q.clave] = valor;
      this.historial.push(q.clave);

      /* La p1 decide qué variante corresponde de acá en adelante (CUSTOM.md §38), así
         que el encuadre se rehace ANTES de recalcular: si no, el número del
         medidor se movería con una respuesta que ya no está en juego. */
      if (q.clave === 'p1') this.reencuadrar();

      const despues = calcular(this.respuestas, this.config);

      // La reacción es el enganche: cada respuesta dice algo, no solo suma.
      const op = q.opciones.find((o) => o.valor === valor);
      this.reaccion = op ? op.reaccion : '';

      // Cuánto bajó la salud con esta respuesta. Es el momento en que el
      // visitante ve que su sistema tiene un problema real.
      this.delta = despues.salud - antes.salud;

      this.paso++;

      /* Un local no tiene fosa ni dren: las cinco preguntas que siguen son
         sobre un sistema que no tiene, y la respuesta al final es siempre la
         misma. Se salta directo al resultado, que para comercios es pedir
         cotización (§5.7.2).

         Se hace moviendo `paso` al final en vez de con un camino de render
         aparte: así `terminado` se vuelve true y todo lo demás —el arco, los
         puntos, el panel— funciona igual que en una encuesta completa. */
      if (q.clave === 'p1' && this.vaACotizacion()) {
        this.saltoComercio = true;
        this.paso = this.preguntas.length;
      }

      this.pintar({ enfocar: true });
    }

    /** ¿La p1 manda a cotización y el bloque está configurado? */
    vaACotizacion() {
      const C = this.textos.comercio || {};
      return this.respuestas.p1 === 'comercio' && !!C.activo && !!C.url;
    }

    volver() {
      if (this.paso === 0) return;

      /* Si se saltó al final, "volver" tiene que devolver a la p1 y no a la
         última pregunta: esa nunca se contestó. */
      if (this.saltoComercio) {
        this.saltoComercio = false;
        this.restaurarPanel();
        this.paso = 0;
        this.historial.pop();
        delete this.respuestas.p1;
        this.preguntas = this.resolverPreguntas();
        this.reaccion = '';
        this.delta = 0;
        if (this.rx) this.rx.hidden = true;
        this.pintar({ enfocar: true });
        return;
      }

      this.paso--;
      const clave = this.historial.pop();
      if (clave) delete this.respuestas[clave];
      // Deshacer la p1 devuelve el cuestionario a sus variantes por defecto.
      this.preguntas = this.resolverPreguntas();
      this.reaccion = '';
      this.delta = 0;
      this.pintar({ enfocar: true });
    }

    reiniciar() {
      // Reiniciar es decir "eso ya no vale": el bot no puede seguir viéndolo.
      try { localStorage.removeItem('nma-diagnostico'); } catch (e) {}
      this.paso = 0;
      this.saltoComercio = false;
      this.restaurarPanel();
      this.respuestas = {};
      this.historial = [];
      this.preguntas = this.resolverPreguntas();
      this.reaccion = '';
      this.delta = 0;
      this.saludPintada = null;
      this.varianteElegida = null;
      if (this.rx) this.rx.hidden = true;
      const est = this.querySelector('[data-rx-estado]');
      if (est) est.textContent = '';
      this.tratamiento = null;
      this.pintar({ enfocar: true });
    }

    // `enfocar` es OPT-IN a propósito. Enfocar mueve el scroll, así que un
    // pintado que no venga de un clic deja la página abierta en la encuesta en
    // vez de arriba. Pasa desapercibido —parece que el sitio carga mal, no que
    // haya un foco de más— y era el bug del montaje inicial.
    //
    // Con el default en false, una llamada nueva desde el montaje o desde un
    // resize no lo puede reintroducir; lo peor que puede pasar es que una
    // llamada nueva desde una interacción se olvide de pedirlo, que se nota al
    // tabular y se arregla en una línea.
    pintar({ enfocar = false } = {}) {
      const total = this.preguntas.length;

      /* 🔴 GUARDADO PROGRESIVO para el asistente.
         Antes solo se guardaba al terminar. Quien contestaba dos o tres
         preguntas y abría el chat —que es justo cuando alguien tiene una duda—
         no le dejaba nada al bot, y el bot le volvía a preguntar lo que ya
         había contestado. Medido en producción: 0 de 28 conversaciones reales
         llegaron con la encuesta.

         Va acá y no en elegir(): pintar() corre después de elegir, de volver y
         de reiniciar, así que cubre los tres. Con la encuesta terminada no
         guarda nada: mostrarResultado() guarda la versión completa, con la
         receta del motor, y no hay que pisarla.

         Con cero respuestas tampoco: pintar() corre al cargar la página, y
         borrar ahí haría que volver a la home le quite al bot una encuesta que
         la persona SÍ terminó antes. */
      if (!this.terminado && Object.keys(this.respuestas).length) {
        this.guardarParaElBot(null, { parcial: true });
      }

      if (this.puntos) {
        this.puntos.innerHTML = this.preguntas
          .map((_, n) => '<i' + (n <= this.paso - 1 || this.terminado ? ' class="is-on"' : '') + '></i>')
          .join('');
      }

      const parcial = calcular(this.respuestas, this.config);

      /* 🔴 En la rama comercial el medidor se apaga ANTES de pintarlo. Pintarlo
         y limpiarlo después no alcanzaba: `moverArco` deja el arco lleno y con
         `data-banda="sano"`, o sea un anillo verde entero, que es justo la
         afirmación que `mostrarResultadoComercio` existe para no hacer. Con un
         solo click no hay nada medido; tampoco un arco que lo insinúe. */
      if (this.saltoComercio) this.apagarMedidor();
      else this.moverArco(parcial, this.terminado);

      if (this.terminado) return this.mostrarResultado(parcial);

      const q = this.preguntas[this.paso];
      const ultima = this.paso === total - 1;
      const encabezado = ultima && this.textos.ultima
        ? this.textos.ultima
        : (this.textos.paso || 'Paso {n} de {total}')
            .replace('{n}', this.paso + 1)
            .replace('{total}', total);

      this.caja.innerHTML =
        '<p class="nma-diag__kicker">' +
        this.escape(encabezado + (q.categoria ? ' · ' + q.categoria : '')) +
        '</p>' +
        '<h3 class="nma-diag__pregunta">' +
        this.escape(q.texto) +
        '</h3>' +
        '<div class="nma-diag__opciones" role="group" aria-label="' +
        this.escape(q.texto) +
        '">' +
        q.opciones
          .map(
            (o) =>
              '<button type="button" class="nma-diag__opcion" data-opcion="' +
              this.escape(o.valor) +
              '">' +
              this.escape(o.texto) +
              '</button>'
          )
          .join('') +
        '</div>';

      if (this.btnVolver) this.btnVolver.hidden = this.paso === 0;

      // La reacción a la respuesta anterior pesa más que un "Calculando…":
      // le dice al visitante qué significó lo que acaba de contestar.
      if (this.estado) {
        this.estado.textContent =
          this.paso === 0
            ? this.textos.estado_inicial || ''
            : this.textoBanda(parcial).nombre;
      }
      if (this.nota) {
        this.nota.textContent =
          this.paso === 0
            ? this.textos.nota_inicial || ''
            : this.reaccion || this.textos.calculando || '';
      }

      this.mostrarDelta();

      // Entrada de la pregunta: da la sensación de avanzar de nivel.
      if (!this.menosMovimiento) {
        this.caja.classList.remove('is-entrando');
        void this.caja.offsetWidth;
        this.caja.classList.add('is-entrando');
      }

      // El foco va a la primera opción: con teclado se avanza sin tabular de
      // más. Solo cuando el pintado viene de una interacción — ver arriba.
      if (enfocar) {
        this.caja.querySelector('.nma-diag__opcion')?.focus({ preventScroll: true });
      }
    }

    /* CUSTOM.md §38 · Nombre y nota de la banda EN EL IDIOMA DEL SISTEMA declarado.

       El id de la banda no cambia —`avanzada` sigue siendo `avanzada`, con
       los mismos cortes, el mismo Ultra y la misma intervención—; lo que
       cambia es cómo se le nombra al visitante. Decirle "saturación" a quien
       está conectado al alcantarillado describe algo que no tiene: la
       saturación es un dren que dejó de infiltrar en la tierra. */
    textoBanda(res) {
      const base = (this.textos.bandas || {})[res.banda.id] || {};
      const porSistema =
        ((this.textos.bandas_por_sistema || {})[this.respuestas.p1] || {})[res.banda.id] || {};
      return {
        nombre: porSistema.nombre || base.nombre || res.banda.nombre,
        nota: porSistema.nota || base.nota || '',
      };
    }

    get menosMovimiento() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    moverArco(res, firme) {
      const salud = this.paso === 0 && !firme ? null : res.salud;

      if (this.arco) {
        const s = salud === null ? 0 : salud;
        this.arco.style.strokeDashoffset = String(LARGO_ARCO - (LARGO_ARCO * s) / 100);
        this.arco.setAttribute('data-banda', res.banda.id);
      }
      if (this.rx) this.rx.setAttribute('data-banda', res.banda.id);
      this.setAttribute('data-banda', res.banda.id);

      if (!this.numero) return;

      if (salud === null) {
        this.saludPintada = null;
        this.numero.innerHTML = '&mdash;<span>/100</span>';
        return;
      }

      const desde = this.saludPintada === null || this.saludPintada === undefined ? 100 : this.saludPintada;
      this.saludPintada = salud;
      this.animarNumero(desde, salud);
    }

    /* El número cuenta hasta su valor en vez de saltar. Es la diferencia entre
       leer un dato y ver algo pasar: sin esto el medidor no engancha. */
    animarNumero(desde, hasta) {
      cancelAnimationFrame(this.animacion);

      const escribir = (v) => {
        this.numero.innerHTML = Math.round(v) + '<span>/100</span>';
      };

      if (this.menosMovimiento || desde === hasta) return escribir(hasta);

      const dur = 600;
      const t0 = performance.now();
      const paso = (t) => {
        const p = Math.min(1, (t - t0) / dur);
        // easing suave al final, para que el número "aterrice"
        const e = 1 - Math.pow(1 - p, 3);
        escribir(desde + (hasta - desde) * e);
        if (p < 1) this.animacion = requestAnimationFrame(paso);
      };
      this.animacion = requestAnimationFrame(paso);
    }

    /* Cuánto costó la última respuesta, en salud. Aparece un instante al lado
       del número: es el momento en que se ve que el problema es real. */
    mostrarDelta() {
      if (!this.marcaDelta) return;
      const d = this.delta || 0;
      if (!d) {
        this.marcaDelta.hidden = true;
        return;
      }
      this.marcaDelta.hidden = false;
      this.marcaDelta.textContent = (d > 0 ? '+' : '') + d;
      this.marcaDelta.dataset.signo = d < 0 ? 'baja' : 'sube';
      this.marcaDelta.classList.remove('is-animada');
      // reflow para poder reiniciar la animación en respuestas seguidas
      void this.marcaDelta.offsetWidth;
      this.marcaDelta.classList.add('is-animada');
    }

    /* Deja las respuestas para el asistente (§ contexto del bot).
       Es la misma pestaña y el mismo dominio, así que localStorage alcanza y no
       hace falta que el dato viaje a ningún lado hasta que la persona escriba.

       No se guarda nada personal: son códigos de opción sobre un desagüe.
       Si localStorage está bloqueado —modo privado, cookies rechazadas— no
       pasa nada: el bot simplemente pregunta como antes. */
    guardarParaElBot(res, { parcial = false } = {}) {
      /* 🔴 SOLO CODIGOS Y NUMEROS. Nada de texto libre.

         Esto pasa por localStorage, o sea que el visitante puede editarlo antes
         de que el bot lo lea. Si mandaramos la frase de dosis ya armada, esa
         frase entraria al prompt como si fuera del sitio, y cualquiera podria
         escribir ahi lo que quisiera.

         Asi que va la RECETA, no el texto: el handle del producto que eligio el
         motor, los gramos, los dias, la banda. El Worker busca el handle en el
         catalogo en vivo y usa el titulo de la tienda, no uno que venga de acá;
         y arma la frase con los numeros, despues de validarlos. Lo peor que
         puede lograr alguien editando esto es recomendarse a si mismo otro
         producto del catalogo, que es lo mismo que puede hacer navegando. */
      var receta = null;
      /* A medias no hay receta: el motor todavía no calculó nada, y un
         `this.tratamiento` que quedó de una vuelta anterior describiría
         respuestas que la persona ya cambió. */
      var tr = parcial ? null : this.tratamiento;
      if (tr) {
        receta = {
          producto: this.handleDe(tr.producto),
          alternativa: this.handleDe(tr.alternativa),
          envase_gramos: tr.envase_gramos || 0,
          frecuencia_dias: tr.frecuencia_dias || 0,
          afirma_dosis: !!tr.afirma_dosis,
          es_ultra: !!tr.es_ultra
        };
      }
      if (res && res.banda) {
        receta = receta || {};
        receta.banda = res.banda.id;
        receta.salud = Math.round(res.salud);
        receta.dolencia = res.dolencia || null;
      }

      try {
        localStorage.setItem('nma-diagnostico', JSON.stringify({
          cuando: Date.now(),
          respuestas: this.respuestas,
          completa: !parcial,
          recomendacion: receta
        }));
      } catch (e) {}
    }

    /* El handle es la ultima parte de /products/<handle>. Se manda el handle y
       no el titulo porque el handle se puede COMPROBAR contra el catalogo. */
    handleDe(item) {
      if (!item || !item.url) return null;
      var m = String(item.url).match(/\/products\/([a-z0-9][a-z0-9-]*)/i);
      return m ? m[1].toLowerCase() : null;
    }

    mostrarResultado(res) {
      this.guardarParaElBot(res);
      /* 🔴 Con un solo click no se puede afirmar nada sobre el sistema de nadie.
         El salto a cotización contesta la p1 y nada más, así que la banda daría
         "Sin señales de saturación — 100/100": no porque el local esté sano,
         sino porque no le preguntamos. Decírselo sería inventar un diagnóstico.

         Es la misma regla que el asistente tiene como prohibición 7: no afirmar
         el estado del sistema del visitante. */
      if (this.saltoComercio) return this.mostrarResultadoComercio();

      const b = this.textoBanda(res);

      if (this.estado) {
        this.estado.textContent = b.nombre;
        // El veredicto entra con un sello: cierra el recorrido.
        if (!this.menosMovimiento) {
          this.estado.classList.remove('is-sellado');
          void this.estado.offsetWidth;
          this.estado.classList.add('is-sellado');
        }
      }
      if (this.nota) this.nota.textContent = b.nota || '';
      if (this.btnVolver) this.btnVolver.hidden = true;
      if (this.marcaDelta) this.marcaDelta.hidden = true;

      this.caja.innerHTML =
        '<p class="nma-diag__kicker">' +
        this.escape(this.textos.listo || 'Diagnóstico listo') +
        '</p>' +
        '<h3 class="nma-diag__pregunta">' +
        this.escape(b.nombre + ' — ' + res.salud + '/100') +
        '</h3>' +
        (b.nota ? '<p class="nma-diag__nota-larga">' + this.escape(b.nota) + '</p>' : '') +
        '<div class="nma-diag__opciones">' +
        '<button type="button" class="nma-diag__opcion" data-reiniciar>' +
        this.escape(this.textos.repetir || 'Repetir el diagnóstico') +
        '</button></div>' +
        (this.textos.pie ? '<p class="nma-diag__pie">' + this.escape(this.textos.pie) + '</p>' : '');

      this.armarTratamiento(res);

      this.dispatchEvent(
        new CustomEvent('nma:diagnostico', {
          bubbles: true,
          detail: { resultado: res, respuestas: Object.assign({}, this.respuestas) },
        })
      );
    }

    /**
     * Deja el medidor en blanco: arco vacío, número escondido y sin banda en
     * ningún lado. Es lo contrario de `moverArco`, no una variante suya — acá
     * no hay valor que mostrar.
     */
    apagarMedidor() {
      cancelAnimationFrame(this.animacion);
      this.saludPintada = null;
      if (this.arco) {
        this.arco.style.strokeDashoffset = String(LARGO_ARCO);
        this.arco.removeAttribute('data-banda');
      }
      if (this.rx) this.rx.removeAttribute('data-banda');
      this.removeAttribute('data-banda');
      if (this.numero) this.numero.hidden = true;
    }

    /** Deshace lo que apaga la rama comercial, para volver o reiniciar. */
    restaurarPanel() {
      if (this.numero) this.numero.hidden = false;
      const kicker = this.rx && this.rx.querySelector('.nma-diag__rx-kicker');
      if (kicker) kicker.hidden = false;
      const introRx = this.querySelector('[data-rx-intro]');
      if (introRx) introRx.hidden = false;
      const zona = this.querySelector('[data-rx-comercio]');
      if (zona) zona.hidden = true;
    }

    /**
     * Resultado de la rama comercial. Sin banda, sin puntaje y sin arco: no hay
     * nada medido. Solo el encuadre y el botón de cotización.
     */
    mostrarResultadoComercio() {
      this.guardarParaElBot();
      const C = this.textos.comercio || {};

      if (this.estado) {
        this.estado.textContent = C.titulo || '';
        this.estado.classList.remove('is-sellado');
      }
      if (this.nota) this.nota.textContent = '';
      if (this.btnVolver) this.btnVolver.hidden = false; // se puede corregir la p1
      if (this.marcaDelta) this.marcaDelta.hidden = true;

      /* El medidor entero, no solo el número: dejarlo en "—/100" —o peor, con
         el arco lleno— insinúa un puntaje que nadie calculó. Lo apaga `pintar`
         antes de llegar acá; se repite por si algún día se entra por otra vía. */
      this.apagarMedidor();

      this.caja.innerHTML =
        '<p class="nma-diag__kicker">' +
        this.escape(this.textos.listo || 'Diagnóstico listo') +
        '</p>' +
        '<h3 class="nma-diag__pregunta">' +
        this.escape(C.titulo || 'Tu caso lo cotizamos') +
        '</h3>' +
        (C.texto ? '<p class="nma-diag__nota-larga">' + this.escape(C.texto) + '</p>' : '') +
        '<div class="nma-diag__opciones">' +
        '<button type="button" class="nma-diag__opcion" data-reiniciar>' +
        this.escape(this.textos.repetir || 'Empezar de nuevo') +
        '</button></div>';

      this.armarTratamiento({ dolencia: 'trampa-grasera', banda: { id: 'sano' }, salud: null });

      this.dispatchEvent(
        new CustomEvent('nma:diagnostico', {
          bubbles: true,
          detail: { resultado: null, comercio: true, respuestas: Object.assign({}, this.respuestas) },
        })
      );
    }

    /* ==================================================================
       Panel "Tu tratamiento" (§5.2)
       ================================================================== */

    /**
     * Elige el producto entre los candidatos que renderizó Liquid.
     * Nunca por ID: por dolencia declarada y rango de severidad (§2.3).
     * Devuelve null si no hay ninguno — que es el estado de hoy, porque los
     * metafields no están cargados. En ese caso se cae al repliegue de §2.4 y
     * NO se muestra un producto cualquiera.
     */
    /* Cero candidatos con una colección que SÍ tiene productos no es el
       repliegue de §2.4: es una carga de metafields mal hecha. El síntoma
       típico es `nma.formato_gramos` a nivel producto en vez de variante — el
       bucle descarta todas las variantes y el resultado enlaza a la colección
       sin que nada se vea roto. */
    avisarCatalogoMudo() {
      const caja = this.querySelector('[data-candidatos]');
      if (!caja) return;
      const enColeccion = parseInt(caja.dataset.origenProductos, 10) || 0;
      if (enColeccion > 0 && this.candidatos.length === 0) {
        console.warn(
          '[nma-diagnostico] La colección de candidatos tiene ' + enColeccion +
            ' producto(s) y no se emitió ningún candidato. Revisá que ' +
            '`nma.formato_gramos` esté a nivel VARIANTE y que los productos ' +
            'declaren `nma.dolencia`.'
        );
      }
    }

    /* ──────────────────────────────────────────────────────────────────
       Elige entre los candidatos que renderizó Liquid. Nunca por ID.

       DOS FAMILIAS, una por régimen (§5.8 y catálogo §3.2):
         · `variante` — un formato de un producto base. Tamaño = `data-gramos`,
           del metafield de VARIANTE. Es lo de mantención.
         · `bundle`   — un pack. Declara aparte cuánto aporta a la intervención
           y cuánto de mantención, porque el protocolo es escalonado.

       Bandas 1 y 2 → variante base. Bandas 3 y 4 con síntoma → bundle.
       ────────────────────────────────────────────────────────────────── */
    elegirProducto(res) {
      const dolenciasDe = (c) => (c.dataset.dolencias || '').split(',').map((d) => d.trim());
      const gramosDe = (c) => parseInt(c.dataset.gramos, 10) || 0;
      const intervDe = (c) => parseInt(c.dataset.intervencion, 10) || 0;
      const ultraDe = (c) => c.dataset.ultra === 'true';
      const esBundle = (c) => c.dataset.tipo === 'bundle';

      const deLaDolencia = this.candidatos.filter(
        (c) => dolenciasDe(c).indexOf(res.dolencia) !== -1
      );
      if (!deLaDolencia.length) return { principal: null, alternativa: null };

      /* El rango de severidad dejó de ser filtro duro: con dos productos base
         ya no discrimina, y su ausencia no debe excluir a nadie. */
      const enRango = (c) => {
        const min = parseInt(c.dataset.smin, 10);
        const max = parseInt(c.dataset.smax, 10);
        return (
          res.severidad >= (isNaN(min) ? 0 : min) && res.severidad <= (isNaN(max) ? 100 : max)
        );
      };

      const mismaConcentracion = (lista) => {
        const iguales = lista.filter((c) => ultraDe(c) === res.esUltra);
        return iguales.length ? iguales : lista;
      };

      // ── Intervención: manda el bundle ──
      if (res.hayIntervencion) {
        const techo = res.intervencionGramos;
        const bundles = mismaConcentracion(
          deLaDolencia.filter(
            (c) => esBundle(c) && enRango(c) && intervDe(c) > 0 && intervDe(c) <= techo
          )
        ).sort((a, b) => intervDe(b) - intervDe(a));

        if (bundles.length) return { principal: bundles[0], alternativa: bundles[1] || null };

        /* Sin bundle para esa dolencia se cae a variante base bajo el mismo
           techo. Lo respalda el catálogo §3.2: el balde de 3 kg trae lo mismo
           que el pack Shock Dren y cuesta igual. Nunca por encima del techo. */
        const sueltas = mismaConcentracion(
          deLaDolencia.filter(
            (c) => !esBundle(c) && enRango(c) && gramosDe(c) > 0 && gramosDe(c) <= techo
          )
        ).sort((a, b) => gramosDe(b) - gramosDe(a));
        return { principal: sueltas[0] || null, alternativa: sueltas[1] || null };
      }

      // ── Mantención: variante base, tamaño por carga declarada ──
      const techo = res.envaseTecho || res.envaseGramos;
      const pozo = mismaConcentracion(
        deLaDolencia.filter(
          (c) => !esBundle(c) && enRango(c) && gramosDe(c) > 0 && gramosDe(c) <= techo
        )
      );
      if (!pozo.length) return { principal: null, alternativa: null };

      const exacto = pozo.find((c) => gramosDe(c) === res.envaseGramos);
      const principal =
        exacto || pozo.slice().sort((a, b) => gramosDe(b) - gramosDe(a))[0] || null;
      if (!principal) return { principal: null, alternativa: null };

      /* §5.6 · alternativa de FORMATO, no de precio: un envase menor para
         empezar. Nunca uno mayor al que la ficha indica para esa carga. */
      const menores = pozo
        .filter((c) => c !== principal && gramosDe(c) > 0 && gramosDe(c) < gramosDe(principal))
        .sort((a, b) => gramosDe(b) - gramosDe(a));

      return { principal, alternativa: menores[0] || null };
    }

    /**
     * Rama comercial (§5.7.2). Devuelve true si se hizo cargo del resultado.
     *
     * Requiere URL: sin formulario adónde mandar, mostrar un botón muerto es
     * peor que el repliegue viejo. Por eso la ausencia de URL no rompe nada,
     * deja el comportamiento anterior.
     */
    mostrarComercio() {
      const C = this.textos.comercio || {};
      const zona = this.querySelector('[data-rx-comercio]');
      if (!zona) return false;
      if (this.respuestas.p1 !== 'comercio' || !C.activo || !C.url) {
        zona.hidden = true;
        return false;
      }

      /* Las respuestas viajan en la URL para que quien cotiza no tenga que
         volver a preguntar lo que la persona ya contestó. Son respuestas sobre
         un desagüe, no datos personales: no hay nada que anonimizar. */
      let destino = C.url;
      try {
        const u = new URL(C.url, window.location.origin);
        Object.keys(this.respuestas).forEach((k) => u.searchParams.set(k, this.respuestas[k]));
        u.searchParams.set('origen', 'diagnostico-comercio');
        /* 🔴 Solo se recorta a ruta si el destino es de esta tienda. El setting
           es de tipo `url`, que acepta un dominio ajeno —un formulario de
           GoHighLevel, por ejemplo, que este proyecto ya usa—, y recortar eso
           a `u.pathname` dejaba el botón apuntando a un 404 de bactoboost.cl.
           La ruta relativa se conserva para el caso propio porque sobrevive a
           un cambio de dominio. */
        destino = u.origin === window.location.origin ? u.pathname + u.search + u.hash : u.href;
      } catch (e) {
        /* URL rara en el setting: se usa tal cual antes que romper el panel. */
      }

      /* "Tu tratamiento" y su intro asumen que abajo viene un producto con una
         dosis. Acá viene una cotización: el encabezado sobra y confunde. */
      const kicker = this.rx.querySelector('.nma-diag__rx-kicker');
      if (kicker) kicker.hidden = true;
      const introRx = this.querySelector('[data-rx-intro]');
      if (introRx) introRx.hidden = true;

      const titulo = this.querySelector('[data-rx-comercio-titulo]');
      const texto = this.querySelector('[data-rx-comercio-texto]');
      const cta = this.querySelector('[data-rx-comercio-cta]');
      if (titulo) titulo.textContent = C.titulo || '';
      if (texto) texto.textContent = C.texto || '';
      if (cta) {
        cta.href = destino;
        cta.textContent = C.cta || 'Pedir cotización';
      }
      zona.hidden = false;

      /* Se apaga todo lo que asume que hay un producto: sin esto queda el
         panel de dosis de la consulta anterior colgado debajo. */
      ['[data-rx-producto]', '[data-rx-repliegue]', '[data-rx-alternativa]', '[data-rx-dosis]']
        .forEach((sel) => {
          const el = this.querySelector(sel);
          if (el) el.hidden = true;
        });
      const btn = this.querySelector('[data-rx-agregar]');
      if (btn) btn.hidden = true;
      this.varianteElegida = null;
      this.candidatos.forEach((c) => (c.hidden = true));

      return true;
    }

    armarTratamiento(res) {
      if (!this.rx) return;
      const T = this.textos.rx || {};
      const cfg = (this.textos.dolencias || {})[res.dolencia] || {};
      const { principal, alternativa } = this.elegirProducto(res);

      this.rx.hidden = false;

      const intro = this.querySelector('[data-rx-intro]');
      if (intro) intro.textContent = T.intro || '';

      /* ---- rama comercial: cotización, no producto ----------------------
         Un local no se resuelve con un producto de góndola. La cantidad
         depende de cuántos desagües hay y de la trampa grasera, y la ficha
         técnica no respalda una dosis para ese caso (§5.7.2) — el propio
         motor ya lo declara con `sin_respaldo`.

         Antes eso terminaba en el repliegue de colección, que para un
         restaurante no dice nada: le mostraba una grilla de productos
         domésticos sin ninguna cantidad. Ahora va derecho a pedir cotización,
         que es la respuesta útil.

         Sale ANTES de elegir producto a propósito: no hay producto que
         elegir. Si el bloque está apagado o sin URL, sigue el camino viejo. */
      if (this.mostrarComercio()) return;

      // ---- producto o repliegue ----
      const zona = this.querySelector('[data-rx-producto]');
      const repliegue = this.querySelector('[data-rx-repliegue]');
      const btnAgregar = this.querySelector('[data-rx-agregar]');

      this.candidatos.forEach((c) => (c.hidden = true));

      if (principal) {
        principal.hidden = false;
        zona.hidden = false;
        zona.innerHTML = '';
        zona.appendChild(principal);
        repliegue.hidden = true;

        const disponible = principal.dataset.disponible === 'true';
        this.varianteElegida = disponible ? principal.dataset.variante : null;
        btnAgregar.hidden = !disponible;
      } else {
        // §2.4: colección, nunca un producto equivocado.
        zona.hidden = true;
        btnAgregar.hidden = true;
        this.varianteElegida = null;

        const enlace = this.querySelector('[data-rx-coleccion]');
        const aviso = this.querySelector('[data-rx-aviso]');
        if (cfg.coleccion_url) {
          repliegue.hidden = false;
          enlace.href = cfg.coleccion_url;
          enlace.textContent = T.ver_coleccion || 'Ver productos';
          if (aviso) aviso.textContent = cfg.nota || '';
        } else {
          repliegue.hidden = true;
        }
      }

      // ---- alternativa de envase menor (§5.6) ----
      const zonaAlt = this.querySelector('[data-rx-alternativa]');
      if (zonaAlt) {
        if (alternativa) {
          zonaAlt.hidden = false;
          this.querySelector('[data-rx-alternativa-texto]').textContent = T.alternativa || '';
          const a = this.querySelector('[data-rx-alternativa-enlace]');
          a.href = alternativa.dataset.url;
          a.textContent = alternativa.dataset.titulo;
        } else {
          zonaAlt.hidden = true;
        }
      }

      // ---- dosis: eje C + eje A, nunca estimada (§5.7) ----
      const zonaDosis = this.querySelector('[data-rx-dosis]');
      const dosis = this.armarDosis(res, cfg, principal);
      if (zonaDosis) {
        zonaDosis.hidden = !dosis;
        if (dosis) this.querySelector('[data-rx-dosis-texto]').textContent = dosis;
      }

      this.tratamiento = {
        dosis: dosis || '',
        envase_gramos: res.envaseGramos,
        es_ultra: res.esUltra,
        afirma_dosis: res.afirmaDosis,
        frecuencia_dias: parseInt(cfg.frecuencia_dias, 10) || 0,
        producto: principal
          ? { titulo: principal.dataset.titulo, url: principal.dataset.url, variante: principal.dataset.variante }
          : null,
        alternativa: alternativa ? { titulo: alternativa.dataset.titulo, url: alternativa.dataset.url } : null,
        coleccion: principal ? null : { url: cfg.coleccion_url || '', titulo: cfg.coleccion_titulo || '' },
      };

      /* Recien acá existe la recomendación. El guardado de mostrarResultado()
         ocurre antes que esto, así que sin esta segunda pasada el bot recibiría
         las respuestas pero no lo que el motor decidió con ellas — que es
         justamente lo que no queremos que vuelva a deducir por su cuenta. */
      this.guardarParaElBot(res);
    }

    /**
     * Texto de dosis. Se compone de la matriz de la ficha (§5.7), no se
     * estima y no sale de la banda: la banda solo decide la concentración.
     *
     * Cuando la ficha no respalda una dosis para ese sistema — comercios y
     * trampas graseras (§5.7.2), y el caso doméstico de alcantarillado
     * (§5.7.1) — el resultado NO afirma una cantidad: muestra el producto y
     * deriva a asesoría. Preferimos no decir nada antes que inventar un número.
     */
    /**
     * Pone en palabras una cantidad en gramos.
     *
     * Prefiere el rótulo que la clienta escribió en el schema, y si no existe
     * lo compone. Hace falta porque desde la v1.5 la dosis de intervención se
     * edita por dolencia: si el validador técnico la baja a 1.500 g, no hay
     * rótulo escrito para ese valor y el texto no puede quedar vacío.
     */
    rotularGramos(g, D) {
      const gramos = parseInt(g, 10);
      if (!gramos) return '';
      const escrito = (D.envases || {})[String(gramos)];
      if (escrito) return escrito;
      if (gramos < 1000) return gramos + ' g';
      const kg = gramos / 1000;
      // Coma decimal, que es lo que se usa en Chile.
      return (Number.isInteger(kg) ? String(kg) : kg.toFixed(1).replace('.', ',')) + ' kg';
    }

    armarDosis(res, cfg, elegido) {
      const D = this.textos.dosificacion || {};

      /* La concentración del texto sale del PRODUCTO ELEGIDO, no de la banda.
         La banda pide Ultra desde saturación avanzada, pero el producto que
         termina recomendándose no siempre lo es: si no hay pack para esa
         dolencia se cae a variante base, y esa variante puede ser Bactoboost.
         Leerlo de la banda hacía que el texto prometiera "Bactoboost Ultra"
         mientras el botón agregaba otra cosa al carro.

         Sin producto —el repliegue de §2.4— se usa la banda, que es lo único
         disponible. */
      const esUltra = elegido ? elegido.dataset.ultra === 'true' : res.esUltra;

      /* El método sale de la dolencia (§5.7.3). Los textos conservan los
         máximos de la ficha — sobre todo los de la alternativa 4, que es la
         de pozo absorbente y drenes y tiene límites más estrictos. */
      const metodo = (D.metodos || {})[String(cfg.metodo || '1')] || '';

      if (!res.afirmaDosis || !res.envaseGramos) {
        /* Sin cadencia documentada tampoco hay nada que contar: es el caso de
           comercios y trampas graseras, y va derecho a asesoría (§5.7.2). */
        if (!parseInt(cfg.frecuencia_dias, 10)) return D.sin_respaldo || '';

        /* Con cadencia documentada pero sin dosis escalada por carga —el caso
           doméstico de alcantarillado (§5.7.1)— el método y el horario sí
           están respaldados por la ficha. Se cuenta cómo y cuándo, no cuánto:
           es la diferencia entre callar un dato y inventarlo. */
        return [metodo, D.horario].filter(Boolean).join(' ');
      }

      const envase = this.rotularGramos(res.envaseGramos, D);
      const concentracion = esUltra ? D.producto_ultra : D.producto_normal;
      const partes = [];

      /* Con intervención se cuentan los dos regímenes por separado, en su
         orden real: primero el evento sobre el sistema saturado, después la
         mantención periódica. Mezclarlos en una sola frase es lo que hacía
         que un envase de 500 g conviviera con un método de "3 a 4 kg". */
      if (res.hayIntervencion) {
        const dosisIntervencion = this.rotularGramos(res.intervencionGramos, D);
        const linea = (D.plantilla_intervencion || '')
          .replace('{envase}', dosisIntervencion)
          .replace('{concentracion}', concentracion || '');
        if (linea.trim()) partes.push(linea.trim());

        /* El reparto en semanas se cuenta siempre (§4.5 del catálogo). Lo que
           NO se afirma es concentración diferenciada por semana: "la semana 1
           el Ultra" solo vale para el Pack Shock Restaurador, que mezcla dos
           concentraciones. Shock Dren es todo Bactoboost y el Pack 2 kg todo
           Ultra; con el repliegue a variante base hay una sola. Prometerlo
           sería falso en tres de los cuatro caminos. */
        if (D.reparto) partes.push(D.reparto);

        if (metodo) partes.push(metodo);
        const luego = (D.plantilla_mantencion_posterior || '')
          .replace('{envase}', envase)
          .replace('{concentracion}', concentracion || '');
        if (luego.trim()) partes.push(luego.trim());
      } else {
        const linea = (D.plantilla || '{envase} de {concentracion}.')
          .replace('{envase}', envase)
          .replace('{concentracion}', concentracion || '');
        if (linea.trim()) partes.push(linea.trim());
        if (metodo) partes.push(metodo);
      }

      /* Cuánto dura el tratamiento (§3.3). Va después de la dosis y antes del
         horario: primero cuánto y cómo, después por cuánto tiempo. Si la rama
         no tiene cadencia documentada devuelve 0 y la frase no aparece. */
      const totalGramos = gramosDelTratamiento(res, cfg.frecuencia_dias);
      if (totalGramos > 0 && D.duracion) {
        const frase = D.duracion
          .replace('{meses}', res.mesesTratamiento)
          .replace('{total}', this.rotularGramos(totalGramos, D));
        if (frase.trim()) partes.push(frase.trim());
      }

      if (D.horario) partes.push(D.horario);
      return partes.join(' ');
    }

    /* ──────────────────────────────────────────────────────────────────
       El componente de carro de Dawn, sea cual sea.

       La tienda está en `cart_type: notification`, así que hoy es
       `<cart-notification>`. Pero NO se hardcodea: si mañana se cambia a
       drawer desde el personalizador, esto lo sigue encontrando.

       Y por eso tampoco se hardcodean los nombres de sección — se le piden a
       `getSectionsToRender()` del propio componente. En notification son
       `cart-notification-product`, `cart-notification-button` y
       `cart-icon-bubble`; en drawer son otros. Escribirlos a mano sería
       romperse en silencio el día que alguien toque ese ajuste.
       ────────────────────────────────────────────────────────────────── */
    get carroDeDawn() {
      return (
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer') ||
        null
      );
    }

    async agregarAlCarro() {
      if (!this.varianteElegida) return;
      const T = this.textos.rx || {};
      const btn = this.querySelector('[data-rx-agregar]');
      const estado = this.querySelector('[data-rx-estado]');

      btn.disabled = true;
      if (estado) estado.textContent = T.agregando || '';

      const carro = this.carroDeDawn;
      const cuerpo = { items: [{ id: Number(this.varianteElegida), quantity: 1 }] };
      if (carro && typeof carro.getSectionsToRender === 'function') {
        cuerpo.sections = carro.getSectionsToRender().map((s) => s.id).join(',');
        cuerpo.sections_url = window.location.pathname;
      }

      try {
        const r = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(cuerpo),
        });
        const datos = await r.json();
        if (!r.ok || datos.status) throw new Error(datos.description || 'error');

        /* A PARTIR DE ACÁ EL PRODUCTO YA ESTÁ EN EL CARRO.
           El mensaje de éxito va antes de pintar nada, a propósito: pintar es
           cosmético y no puede convertir un alta hecha en un "no pudimos". */
        if (estado) estado.textContent = T.agregado || '';

        /* Dawn lee `parsedState.key` para armar el selector de la notificación,
           y esa clave solo viene arriba cuando se agrega UN item suelto.
           Nosotros mandamos la forma `items: [...]`, cuya clave vive en
           `items[0]`. Sin esto el selector queda `...-undefined`,
           `querySelector` devuelve null y `renderContents` explota con un
           TypeError — con el producto ya agregado. Era el bug del botón. */
        const paraDawn = Object.assign({}, datos, datos.items && datos.items[0]);

        try {
          if (carro && typeof carro.renderContents === 'function') carro.renderContents(paraDawn);
        } catch (fallaAlPintar) {
          /* El carro quedó bien; lo único que falla es el cartelito. Se avisa
             en consola para no perder el rastro, y se sigue. */
          console.warn('[diagnostico] no se pudo pintar la notificación del carro:', fallaAlPintar);
        }
      } catch (e) {
        if (estado) estado.textContent = T.error || '';
      } finally {
        btn.disabled = false;
      }
    }

    escape(t) {
      const d = document.createElement('div');
      d.textContent = t == null ? '' : String(t);
      return d.innerHTML;
    }
  }

  if (!customElements.get('nma-diagnostico')) {
    customElements.define('nma-diagnostico', NmaDiagnosticoElemento);
  }

  window.NmaDiagnostico = {
    calcular,
    distribucion,
    recorridos,
    gramosDelTratamiento,
    fusionarConfig,
    PREGUNTAS,
    OPCIONES,
    OPCIONES_POR_SISTEMA,
    opcionesDe,
    BANDAS_POR_SISTEMA,
    DOLENCIAS_VALIDAS,
    CONFIG_POR_DEFECTO,
  };
})();
